/**
 * Trace ID generation and propagation for production operability.
 * 
 * Every critical user action should have a traceId that:
 * - Uniquely identifies the operation
 * - Propagates to backend calls
 * - Appears in error reports and Sentry
 * - Helps support identify specific user issues
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * Generate a new trace ID (UUID v4)
 * 
 * @param prefix Optional prefix for easier identification in logs
 * @returns Unique trace ID string
 */
export function newTraceId(prefix: string = 'trace'): string {
  const uuid = uuidv4();
  return `${prefix}-${uuid}`;
}

/**
 * Extract short suffix from trace ID for display
 * Shows last 8 characters for user-friendly display
 */
export function getTraceIdSuffix(traceId: string | undefined): string {
  if (!traceId) return 'puuttuu';
  return traceId.slice(-8).toUpperCase();
}

/**
 * Format trace ID and context for copy-to-clipboard
 */
export function formatTraceForCopy(
  code: string,
  traceId: string | undefined,
  context?: Record<string, unknown>
): string {
  const parts: string[] = [
    `code=${code}`,
    `traceId=${traceId ?? 'puuttuu'}`,
  ];
  
  // Add known context keys if available
  if (context) {
    if (context.playerId) parts.push(`playerId=${context.playerId}`);
    if (context.sessionId) parts.push(`sessionId=${context.sessionId}`);
    if (context.locationId) parts.push(`locationId=${context.locationId}`);
    if (context.questionId) parts.push(`questionId=${context.questionId}`);
    if (context.timePeriodId) parts.push(`timePeriodId=${context.timePeriodId}`);
  }
  
  return parts.join(' ');
}

// Re-export for convenience
export { v4 as generateUUID } from 'uuid';
